import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";

import axios from "../lib/axios";
import {
  DEVICE_TOKEN_KEY,
  getOrCreateInstallationId,
} from "../lib/deviceIdentity";

const VERIFY_TIMEOUT_MS = 2500;
const HEARTBEAT_INTERVAL_MS = 15000;
const TRANSIENT_RETRY_MS = 2000;
const BLOCKED_RETRY_MS = 3000;
const INACTIVE_RETRY_MS = 5000;

function DeviceStatusScreen({ title, description, subtext }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#2b2b2b] text-[#f2f2f2] p-6">
      <div className="w-full max-w-md rounded-2xl bg-[#1f1f1f] shadow-lg p-6 text-center">
        <div className="text-2xl font-bold mb-2">{title}</div>
        <div className="text-gray-300 whitespace-pre-line">{description}</div>
        {subtext ? <div className="text-gray-400 mt-4 text-sm">{subtext}</div> : null}
      </div>
    </div>
  );
}

function getRetryDelay(reason) {
  if (reason === "blocked") {
    return BLOCKED_RETRY_MS;
  }

  if (reason === "inactive") {
    return INACTIVE_RETRY_MS;
  }

  return TRANSIENT_RETRY_MS;
}

function isTransientVerifyFailure(error) {
  const status = error?.response?.status;
  const apiErr = error?.response?.data?.error;

  return !status || status >= 500 || apiErr === "invalid_store" || error?.code === "ECONNABORTED";
}

export default function DeviceGate({ children }) {
  const location = useLocation();

  const [state, setState] = useState({
    loading: true,
    ok: false,
    reason: null, // no_token | blocked | inactive | invalid | updating
  });

  const retryTimerRef = useRef(null);
  const heartbeatTimerRef = useRef(null);
  const okRef = useRef(false);
  const initializedRef = useRef(false);

  useEffect(() => {
    okRef.current = state.ok;
  }, [state.ok]);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
  }, []);

  const stopRetry = useCallback(() => {
    if (retryTimerRef.current) {
      clearInterval(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  const clearTimers = useCallback(() => {
    stopRetry();
    stopHeartbeat();
  }, [stopHeartbeat, stopRetry]);

  const scheduleRetry = useCallback((reason, verifyFn) => {
    if (retryTimerRef.current) {
      return;
    }

    retryTimerRef.current = setInterval(() => {
      verifyFn();
    }, getRetryDelay(reason));
  }, []);

  const verify = useCallback(async () => {
    const token = localStorage.getItem(DEVICE_TOKEN_KEY);
    const installationId = getOrCreateInstallationId();

    if (!okRef.current && !initializedRef.current) {
      setState((prev) => ({ ...prev, loading: true }));
    }

    try {
      const requestConfig = token
        ? {
            headers: { "X-Device-Token": token },
            timeout: VERIFY_TIMEOUT_MS,
          }
        : { timeout: VERIFY_TIMEOUT_MS };

      await axios.post("/device/verify", { installation_id: installationId }, requestConfig);

      initializedRef.current = true;
      stopRetry();

      if (!heartbeatTimerRef.current) {
        heartbeatTimerRef.current = setInterval(() => {
          verify();
        }, HEARTBEAT_INTERVAL_MS);
      }

      setState({ loading: false, ok: true, reason: null });
    } catch (error) {
      const status = error?.response?.status;
      const apiErr = error?.response?.data?.error;

      if (apiErr === "invalid_token") {
        clearTimers();
        localStorage.removeItem(DEVICE_TOKEN_KEY);
        setState({ loading: false, ok: false, reason: "invalid" });
        return;
      }

      if (status === 403 && apiErr === "device_blocked") {
        stopHeartbeat();
        setState({ loading: false, ok: false, reason: "blocked" });
        scheduleRetry("blocked", verify);
        return;
      }

      if (status === 403 && apiErr === "store_inactive") {
        stopHeartbeat();
        setState({ loading: false, ok: false, reason: "inactive" });
        scheduleRetry("inactive", verify);
        return;
      }

      if (okRef.current) {
        scheduleRetry("updating", verify);
        setState((prev) => ({ ...prev, loading: false, ok: true, reason: null }));
        return;
      }

      if (token && isTransientVerifyFailure(error)) {
        stopHeartbeat();
        setState({ loading: false, ok: false, reason: "updating" });
        scheduleRetry("updating", verify);
        return;
      }

      stopHeartbeat();
      setState({ loading: false, ok: false, reason: "no_token" });
    }
  }, [clearTimers, scheduleRetry, stopHeartbeat, stopRetry]);

  useEffect(() => {
    verify();

    const onFocus = () => verify();
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        verify();
      }
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      clearTimers();
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [clearTimers, verify]);

  if (state.loading) {
    return (
      <DeviceStatusScreen
        title="⏳ 잠시만요"
        description="등록된 기기인지 확인하고 있어요."
      />
    );
  }

  if (!state.ok && state.reason === "blocked") {
    return (
      <DeviceStatusScreen
        title="⛔ 차단된 기기입니다"
        description="관리자 화면에서 차단이 해제되면 자동으로 다시 연결돼요."
        subtext="자동으로 다시 확인하는 중입니다."
      />
    );
  }

  if (!state.ok && state.reason === "inactive") {
    return (
      <DeviceStatusScreen
        title="🏪 비활성 매장입니다"
        description="상위 관리자에서 매장을 다시 활성화하면 자동으로 다시 연결돼요."
        subtext="자동으로 다시 확인하는 중입니다."
      />
    );
  }

  if (!state.ok && state.reason === "updating") {
    return (
      <DeviceStatusScreen
        title="🔄 업데이트 중입니다"
        description={
          "배포 또는 서버 재시작으로 잠시 연결이 끊겼을 수 있어요.\n잠시 후 자동으로 다시 연결됩니다."
        }
        subtext="재등록하지 않아도 됩니다. 자동으로 다시 시도하는 중입니다."
      />
    );
  }

  if (!state.ok) {
    return <Navigate to="/enroll" replace state={{ from: location.pathname }} />;
  }

  return children;
}
